import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Autocomplete, Box, Button, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import MainLayout from '../../layouts/MainLayout'
import PageHeader from '../../components/PageHeader'
import baseService from '../../services/baseService'
import api from '../../services/api'

const maskDate = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export default function AuditLogReportPage() {
  const [mode, setMode] = useState('period')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [search, setSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [exportId, setExportId] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearch(userSearch.trim()), 300)
    return () => clearTimeout(timer)
  }, [userSearch])

  const { data: users = [], isFetching: searchingUsers } = useQuery({
    queryKey: ['audit-report-users', search],
    queryFn: async () => (await baseService.getByColumn(`/audit-logs/users?search=${encodeURIComponent(search)}`)).result || [],
    enabled: search.length >= 2,
    staleTime: 30_000,
  })

  const { data: exportStatus, isError: isStatusError } = useQuery({
    queryKey: ['audit-log-report-status', exportId],
    queryFn: async () => (await api.get(`/audit-logs/report-status/${exportId}`)).data.result,
    enabled: !!exportId,
    refetchInterval: (query) => ['pending', 'processing'].includes(query.state.data?.status) ? 2000 : false,
  })

  const generateReport = async () => {
    setErrorMessage('')
    if (mode === 'period' && !from && !to) {
      setErrorMessage('Informe a data inicial, a data final ou ambas para gerar o relatório.')
      return
    }
    if (mode === 'user' && !selectedUser) {
      setErrorMessage('Selecione um usuário para gerar o relatório.')
      return
    }

    setGenerating(true)
    setExportId(null)
    try {
      const response = await api.post('/audit-logs/report', {
        mode,
        from: from || undefined,
        to: to || undefined,
        user_id: selectedUser?.id || undefined,
      })
      setExportId(response.data.result.export_id)
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Não foi possível iniciar a geração do relatório.')
    } finally {
      setGenerating(false)
    }
  }

  const downloadReport = async () => {
    if (!exportId) return
    try {
      const response = await api.get(`/audit-logs/report-download/${exportId}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `relatorio-logs-${exportId}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      setErrorMessage('Não foi possível baixar o PDF. Atualize o status e tente novamente.')
    }
  }

  return <MainLayout>
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <PageHeader title="Relatórios de logs" />
      <Paper component="section" sx={{ p: { xs: 2, md: 3 }, mt: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Gere um relatório PDF das atividades registradas no sistema. A consulta pode ser feita por período ou por usuário; os dois critérios também podem ser combinados.
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, value) => { if (value) { setMode(value); setErrorMessage('') } }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="period">Por data/período</ToggleButton>
          <ToggleButton value="user">Por usuário</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Data inicial" placeholder="dd/mm/aaaa" value={from} onChange={(event) => setFrom(maskDate(event.target.value))} inputProps={{ inputMode: 'numeric', maxLength: 10 }} helperText="Deixe vazia para iniciar pela primeira atividade." />
          <TextField label="Data final" placeholder="dd/mm/aaaa" value={to} onChange={(event) => setTo(maskDate(event.target.value))} inputProps={{ inputMode: 'numeric', maxLength: 10 }} helperText="Deixe vazia para incluir até a última atividade." />
          <Autocomplete
            options={users}
            value={selectedUser}
            inputValue={userSearch}
            loading={searchingUsers}
            filterOptions={(items) => items}
            getOptionLabel={(option) => option?.username ? `${option.username} — ${option.name}` : option?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(_, value, reason) => {
              setUserSearch(value)
              if (reason === 'input' && selectedUser) setSelectedUser(null)
            }}
            onChange={(_, value) => {
              setSelectedUser(value)
              setUserSearch(value ? (value.username ? `${value.username} — ${value.name}` : value.name) : '')
            }}
            noOptionsText={userSearch.trim().length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum usuário encontrado'}
            loadingText="Buscando usuários..."
            renderInput={(params) => <TextField {...params} label={mode === 'user' ? 'Usuário (obrigatório)' : 'Usuário (opcional)'} placeholder="Digite nome ou usuário" />}
          />
        </Box>
        {mode === 'user' && !from && !to && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Sem datas, será incluído todo o histórico disponível para o usuário selecionado.
        </Typography>}
        {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
        {isStatusError && <Alert severity="error" sx={{ mt: 2 }}>Não foi possível consultar o andamento da geração.</Alert>}
        {exportStatus && ['pending', 'processing'].includes(exportStatus.status) && <Alert severity="info" sx={{ mt: 2 }}>
          {exportStatus.status === 'pending' ? 'Relatório na fila de processamento.' : 'Gerando o relatório em segundo plano…'} A página será atualizada automaticamente.
        </Alert>}
        {exportStatus?.status === 'failed' && <Alert severity="error" sx={{ mt: 2 }}>{exportStatus.message || 'Falha ao gerar o relatório.'}</Alert>}
        {exportStatus?.status === 'completed' && <Alert severity="success" sx={{ mt: 2 }}>
          {exportStatus.message || 'Relatório pronto.'} <Button size="small" onClick={downloadReport}>Baixar PDF</Button>
        </Alert>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" onClick={generateReport} disabled={generating}>
            {generating ? 'Gerando PDF…' : 'Gerar relatório PDF'}
          </Button>
        </Box>
      </Paper>
    </Box>
  </MainLayout>
}
